// src/pages/PropertyForm.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import './PropertyForm.css';
import PropertyStatsDisplay from '../components/property/PropertyStatsDisplay'; // Import the new component
import usePropertyImages from '../hooks/usePropertyImages';
import '../components/property/PropertyStatsDisplay.css'; // Import the CSS for the stats display
import { ConfigContext } from '../context/ConfigContext'; // Import ConfigContext
import { UserContext } from '../context/UserContext';
import { NotificationContext } from '../context/NotificationContext';
import Tabs from '../components/Tabs';
import PropertyFields from '../components/property/PropertyFields'; // eslint-disable-line no-unused-vars
import usePropertyApi from '../hooks/usePropertyApi';
import { parseCurrency } from '../utils/currencyFormatter';
import { calcROI } from '../utils/calcROI';
import PropertyPage from './property/PropertyPage'; // Use the new reusable page for previews
import { format } from 'date-fns';
import { PropertyStatusNumeric, PropertyTypeNumericToStringMap } from '../utils/enums';
import { states } from '../utils/states';
import { BASE_URL } from '../utils/config';
import PurchaseModal from '../components/payment/PurchaseModal'; // eslint-disable-line no-unused-vars
import { formatNumberForInput } from '../utils/formatting';
import { apiFetch } from '../utils/apiClient';

// Globally required fields for the entire form
const globalRequiredFields = ['address', 'city', 'state', 'zipCode', 'type', 'price', 'status'];

const tabDefinitions = [
  {
    id: 'overview',
    label: 'Overview & Location',
    fields: ['propertyID', 'address', 'city', 'state', 'zipCode', 'type', 'status', 'description', 'notesToAI', 'mainImageURL', 'propertyImages', 'createdOn', 'location.coordinates', 'location.type'],
  },
  {
    id: 'financials',
    label: 'Financials & ROI',
    fields: ['price', 'inspectionCost', 'closingCosts', 'additionalCosts', 'additionalCostsDescription', 'rent', 'roi'],
  },
  {
    id: 'expenses',
    label: 'Recurring Expenses',
    fields: ['hoa', 'managementFee', 'maintenance', 'utilities', 'vacancyRate', 'propertyTax', 'insurance', 'otherExpenses', 'otherExpensesDescription'],
  },
  {
    id: 'details',
    label: 'Property Details',
    fields: ['beds', 'baths', 'size', 'lotSize', 'tenantInPlace', 'yearBuilt'],
  },
  {
    id: 'inquirySettings',
    label: 'Inquiry Settings',
    fields: ['inquiryRequirements'], // This will represent the group of settings
  },
  {
    id: 'photos',
    label: 'Photos',
    fields: ['mainImageURL', 'propertyImages'],
  },
];

// Define default formData structure as a reusable function
const getDefaultFormData = () => ({
  propertyID: '',
  address: '',
  county: '',
  city: '',
  state: '',
  zipCode: '',
  type: '',
  description: '',
  price: '',
  hoa: '',
  managementFee: '10',
  maintenance: '',
  utilities: '',
  vacancyRate: '',
  propertyTax: '',
  insurance: '',
  rent: {
    min: '',
    max: '',
  },
  beds: '',
  baths: '',
  size: '',
  lotSize: '',
  tenantInPlace: false,
  yearBuilt: '',
  mainImageURL: '',
  propertyImages: [],
  notesToAI: '',
  inspectionCost: '',
  closingCosts: '',
  additionalCosts: '',
  additionalCostsDescription: '',
  otherExpenses: '',
  otherExpensesDescription: '',
  status: PropertyStatusNumeric.ForSale,
  createdOn: '',
  location: {
    type: 'Point',
    coordinates: [],
  },
  roi: {
    min: null,
    max: null,
  },
  featured: 0,
  sellerUsername: '',
  sellerFullName: '',
  featuredUntil: null,
  cachedReports: {},
  stats: null,
  inquiryRequirements: {
    requiresAllCash: false,
    minExperienceLevel: 'All',
    minPurchaseTimeline: 'All',
    minInvolvementLevel: 1,
  },
});

const PropertyForm = ({ mode, property, onDelete, propertyID, onCancel, onEdit, onSuccess }) => {
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext); // Use global notification context
  const { isLoading: isConfigLoading } = useContext(ConfigContext);
  const [formData, setFormData] = useState(null);
  const currentYear = new Date().getFullYear();
  const [displayAddress, setDisplayAddress] = useState('');
  const [activeTab, setActiveTab] = useState(tabDefinitions[0].id);
  const [tabErrors, setTabErrors] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);

  const { isLoading, error, setError, fetchProperty, handleSubmit, handleDelete } = usePropertyApi(
    mode,
    propertyID,
    onCancel,
    onDelete,
    (updatedProperty) => {
      // Custom onSuccess handler to clear staged images after a successful 'add'
      if (mode === 'add') {
        clearStagedImages();
      }
      onSuccess(updatedProperty); // Call the original onSuccess handler from props
    },
    addNotification
  );

  const validateField = (name, value, currentFormData) => {
    if (globalRequiredFields.includes(name)) {
      if (value === '' || value === null || (typeof value === 'number' && isNaN(value))) {
        return `${name.replace(/([A-Z])/g, ' $1').trim()} is required.`;
      }
    }
    if (name === 'location.coordinates' && (!Array.isArray(value) || value.length !== 2 || !value.every((coord) => typeof coord === 'number'))) {
      return 'Valid map coordinates are required. Please select an address from suggestions.';
    }
    return null;
  };

  const resetFormData = useCallback(() => {
    setFormData(getDefaultFormData());
    setDisplayAddress('');
    setError(null);
    setTabErrors({});
  }, [setError]);

  useEffect(() => {
    if (mode === 'edit' || (mode === 'view' && !property)) {
      if (propertyID) {
        console.log(`PropertyForm Effect: mode=${mode}, propertyID=${propertyID}. Fetching property.`);
        const loadProperty = async () => {
          const data = await fetchProperty();
          const defaults = getDefaultFormData();
          if (data) {
            setFormData({
              ...defaults,
              propertyID: data.propertyID || defaults.propertyID,
              address: data.address || defaults.address,
              city: data.city || defaults.city,
              county: data.county || defaults.county,
              state: data.state || defaults.state,
              zipCode: data.zipCode || defaults.zipCode,
              type: PropertyTypeNumericToStringMap[data.type] || defaults.type,
              description: data.description || defaults.description,
              price: data.price || defaults.price,
              hoa: data.hoa || defaults.hoa,
              managementFee: data.managementFee !== undefined && data.managementFee !== null ? String(data.managementFee) : defaults.managementFee,
              maintenance: data.maintenance || defaults.maintenance,
              utilities: data.utilities || defaults.utilities,
              vacancyRate: data.vacancyRate || defaults.vacancyRate,
              propertyTax: data.propertyTax || defaults.propertyTax,
              insurance: data.insurance || defaults.insurance,
              rent: data.rent || { min: '', max: '' },
              beds: data.beds || defaults.beds,
              baths: data.baths || defaults.baths,
              size: data.size || defaults.size,
              lotSize: formatNumberForInput(data.lotSize, 2) || defaults.lotSize,
              tenantInPlace: data.tenantInPlace ?? defaults.tenantInPlace,
              yearBuilt: data.yearBuilt || defaults.yearBuilt,
              mainImageURL: data.mainImageURL || defaults.mainImageURL,
              propertyImages: Array.isArray(data.propertyImages) ? data.propertyImages : defaults.propertyImages,
              notesToAI: data.notesToAI || defaults.notesToAI,
              inspectionCost: data.inspectionCost || defaults.inspectionCost,
              closingCosts: data.closingCosts || defaults.closingCosts,
              additionalCosts: data.additionalCosts || defaults.additionalCosts,
              additionalCostsDescription: data.additionalCostsDescription || defaults.additionalCostsDescription,
              otherExpenses: data.otherExpenses || defaults.otherExpenses,
              otherExpensesDescription: data.otherExpensesDescription || defaults.otherExpensesDescription,
              status: data.status || defaults.status,
              createdOn: data.createdOn || defaults.createdOn,
              location: data.location || defaults.location,
              roi: data.roi || { min: null, max: null },
              featured: data.featured ?? defaults.featured,
              sellerUsername: data.sellerUsername || defaults.sellerUsername,
              sellerFullName: data.sellerFullName || defaults.sellerFullName,
              featuredUntil: data.featuredUntil || defaults.featuredUntil,
              cachedReports: data.cachedReports || defaults.cachedReports,
              stats: data.stats || defaults.stats,
              inquiryRequirements: data.inquiryRequirements || defaults.inquiryRequirements,
            });
            setDisplayAddress(String(data.address || ''));
          } else {
            console.warn(`PropertyForm Effect: Failed to fetch property for ID ${propertyID}. Resetting form.`);
            resetFormData();
            setActiveTab(tabDefinitions[0].id);
          }
        };
        loadProperty();
      } else if (mode === 'edit') {
        console.warn('PropertyForm Effect: Edit mode without propertyID. Resetting form.');
        resetFormData();
        setActiveTab(tabDefinitions[0].id);
      }
    } else if (mode === 'add') {
      console.log('PropertyForm Effect: mode=add. Resetting form.');
      resetFormData();
    }
  }, [mode, propertyID, property, fetchProperty, resetFormData]);

  useEffect(() => {
    if (mode === 'view' && property) {
      const defaults = getDefaultFormData();
      setFormData({
        ...defaults,
        propertyID: property.propertyID || defaults.propertyID,
        address: property.address || defaults.address,
        city: property.city || defaults.city,
        county: property.county || defaults.county,
        state: property.state || defaults.state,
        zipCode: property.zipCode || defaults.zipCode,
        type: PropertyTypeNumericToStringMap[property.type] || defaults.type,
        description: property.description || defaults.description,
        price: property.price || defaults.price,
        hoa: property.hoa || defaults.hoa,
        managementFee: property.managementFee !== undefined && property.managementFee !== null ? String(property.managementFee) : defaults.managementFee,
        maintenance: property.maintenance || defaults.maintenance,
        utilities: property.utilities || defaults.utilities,
        vacancyRate: property.vacancyRate || defaults.vacancyRate,
        propertyTax: property.propertyTax || defaults.propertyTax,
        insurance: property.insurance || defaults.insurance,
        rent: property.rent || { min: '', max: '' },
        beds: property.beds || defaults.beds,
        baths: property.baths || defaults.baths,
        size: property.size || defaults.size,
        lotSize: formatNumberForInput(property.lotSize, 2) || defaults.lotSize,
        tenantInPlace: property.tenantInPlace ?? defaults.tenantInPlace,
        yearBuilt: property.yearBuilt || defaults.yearBuilt,
        mainImageURL: property.mainImageURL || defaults.mainImageURL,
        propertyImages: Array.isArray(property.propertyImages) ? property.propertyImages : defaults.propertyImages,
        notesToAI: property.notesToAI || defaults.notesToAI,
        inspectionCost: property.inspectionCost || defaults.inspectionCost,
        closingCosts: property.closingCosts || defaults.closingCosts,
        additionalCosts: property.additionalCosts || defaults.additionalCosts,
        additionalCostsDescription: property.additionalCostsDescription || defaults.additionalCostsDescription,
        otherExpenses: property.otherExpenses || defaults.otherExpenses,
        otherExpensesDescription: property.otherExpensesDescription || defaults.otherExpensesDescription,
        status: property.status || defaults.status,
        createdOn: property.createdOn || defaults.createdOn,
        location: property.location || defaults.location,
        roi: property.roi || { min: null, max: null },
        featured: property.featured ?? defaults.featured,
        sellerUsername: property.sellerUsername || defaults.sellerUsername,
        sellerFullName: property.sellerFullName || defaults.sellerFullName,
        featuredUntil: property.featuredUntil || defaults.featuredUntil,
        cachedReports: property.cachedReports || defaults.cachedReports,
        stats: property.stats || defaults.stats,
        inquiryRequirements: property.inquiryRequirements || defaults.inquiryRequirements,
      });
      setDisplayAddress(String(property.address || ''));
    }
  }, [mode, property]);

  useEffect(() => {
    if (formData && (mode === 'add' || mode === 'edit')) {
      const currentTabDef = tabDefinitions.find((t) => t.id === activeTab);
      if (!currentTabDef) return;

      const errorsForCurrentTab = {};
      currentTabDef.fields.forEach((fieldName) => {
        const value = fieldName.includes('.') ? formData[fieldName.split('.')[0]]?.[fieldName.split('.')[1]] : formData[fieldName];
        const error = validateField(fieldName, value, formData);
        if (error) {
          errorsForCurrentTab[fieldName] = error;
        }
      });
      setTabErrors((prev) => ({ ...prev, [activeTab]: errorsForCurrentTab }));
    }
  }, [formData, activeTab, mode]);

  useEffect(() => {
    if (formData && (mode === 'add' || mode === 'edit')) {
      const baseFinancials = {
        price: formData.price,
        hoa: formData.hoa,
        propertyTax: formData.propertyTax,
        insurance: formData.insurance,
        inspectionCost: formData.inspectionCost,
        closingCosts: formData.closingCosts,
        additionalCosts: formData.additionalCosts,
        managementFeePercentage: formData.managementFee,
        maintenance: formData.maintenance,
        utilities: formData.utilities,
        otherExpenses: formData.otherExpenses,
        vacancyRate: formData.vacancyRate,
      };

      const roiMin = formData.rent.min ? calcROI({ ...baseFinancials, rent: formData.rent.min }) : null;
      const roiMax = formData.rent.max ? calcROI({ ...baseFinancials, rent: formData.rent.max }) : null;

      const newRoi = {
        min: roiMin,
        max: roiMax,
      };

      // Check if the calculated ROI range is different from the one in state
      if (formData.roi?.min !== newRoi.min || formData.roi?.max !== newRoi.max) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          roi: newRoi,
        }));
      }
    }
  }, [formData, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested state for inquiryRequirements
    if (name.startsWith('inquiryRequirements.')) {
      const key = name.split('.')[1];
      const parsedValue = name === 'inquiryRequirements.minInvolvementLevel' ? parseInt(value, 10) || 1 : value;

      setFormData((prev) => ({
        ...prev,
        inquiryRequirements: {
          ...prev.inquiryRequirements,
          [key]: type === 'checkbox' ? checked : parsedValue,
        },
      }));
      return;
    }

    // Handle nested state for rent and roi ranges
    if (name.includes('.')) {
      const [mainKey, subKey] = name.split('.');
      if (mainKey === 'rent' || mainKey === 'roi') {
        const parsedValue = parseCurrency(value);
        setFormData((prev) => ({
          ...prev,
          [mainKey]: { ...prev[mainKey], [subKey]: parsedValue },
        }));
        return;
      }
    }

    if (name === 'price') {
      const newPrice = parseCurrency(value);
      const newMaintenanceDefault = Number(newPrice) * 0.01 || '';
      setFormData((prev) => ({
        ...prev,
        price: newPrice,
        maintenance: prev.maintenance === '' || Number(prev.maintenance) === Number(prev.price) * 0.01 ? newMaintenanceDefault : prev.maintenance,
      }));
    } else {
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          [name]: ['price', 'hoa', 'propertyTax', 'insurance', 'rent', 'inspectionCost', 'closingCosts', 'additionalCosts', 'maintenance', 'utilities', 'otherExpenses'].includes(name) ? parseCurrency(value) : type === 'checkbox' ? checked : value,
        };
        console.log(`handleChange: ${name}=${value}, newFormData:`, newFormData);
        return newFormData;
      });
    }
  };

  const handleAddressSelect = (selectedAddressData) => {
    console.log('Selected Address Data:', selectedAddressData);

    if (!selectedAddressData) return;

    if (selectedAddressData.type === 'Feature') {
      const { geometry, properties } = selectedAddressData;
      const coordinates = geometry?.coordinates || [];
      const { address, name } = properties || {};

      console.log('Address components:', { address, name });

      const streetAddress = [address?.house_number, address?.road].filter(Boolean).join(' ') || name || '';
      console.log('Computed streetAddress:', streetAddress);

      const city = address?.city || address?.town || address?.village || address?.hamlet || '';
      console.log('Computed city:', city);

      // Strip " County" from the end of the county name if it exists.
      const rawCounty = address?.county || '';
      const county = rawCounty.endsWith(' County') ? rawCounty.slice(0, -7) : rawCounty;
      console.log('Computed county:', county, '(from raw:', rawCounty, ')');

      const fullState = address?.state || '';
      console.log('Full state name:', fullState);
      const stateObj = states.find((state) => state.label.toLowerCase() === fullState.toLowerCase());
      const state = stateObj ? stateObj.value : '';
      console.log('Converted state to 2-letter code:', state);

      const zipCode = address?.postcode || '';
      console.log('Computed zipCode:', zipCode);

      setFormData((prev) => ({
        ...prev,
        address: streetAddress || prev.address,
        city: city || prev.city,
        county: county || prev.county,
        state: state || prev.state,
        zipCode: zipCode || prev.zipCode,
        location: coordinates.length === 2 ? { type: 'Point', coordinates: coordinates } : prev.location,
      }));
      const newDisplayAddress = String(streetAddress || name || '');
      console.log('Setting displayAddress to:', newDisplayAddress);
      setDisplayAddress(newDisplayAddress);
    }
  };

  const handleCancelClick = () => {
    resetFormData();
    onCancel();
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const handlePreviewClick = () => {
    setIsPreviewOpen(true);
  };

  const handleClearCache = async () => {
    if (!formData.propertyID) {
      addNotification('Property ID is missing.', 'error');
      return;
    }
    if (window.confirm(`Are you sure you want to clear the cached report for property ID ${formData.propertyID}?`)) {
      try {
        const response = await apiFetch(`${BASE_URL}/api/properties/property/${formData.propertyID}/clear-cache`, { method: 'DELETE' }, user.token);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to clear cache.');
        addNotification(data.message, 'success');
        setFormData((prev) => ({
          ...prev,
          cachedReports: {},
        }));
      } catch (err) {
        addNotification(err.message, 'error');
      }
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (mode === 'view') return;

    let allErrors = {};
    let firstErrorTab = null;

    tabDefinitions.forEach((tab) => {
      const errorsOnTab = {};
      tab.fields.forEach((fieldName) => {
        const value = fieldName.includes('.') ? formData[fieldName.split('.')[0]]?.[fieldName.split('.')[1]] : formData[fieldName];
        const error = validateField(fieldName, value, formData);
        if (error) {
          errorsOnTab[fieldName] = error;
          if (!firstErrorTab) firstErrorTab = tab.id;
        }
      });
      allErrors[tab.id] = errorsOnTab;
    });

    setTabErrors(allErrors);

    if (firstErrorTab) {
      console.error('Validation errors found:', allErrors); // Gemini debug
      setActiveTab(firstErrorTab);
      setError('Please correct the errors on the highlighted tabs.');
      return;
    }

    if (mode === 'add') {
      // For new properties, compile the list of staged image IDs to send to the backend.
      const orderedStagedImageIds = [];
      const mainImage = stagedImages.find((img) => img.isMain);
      if (mainImage) {
        orderedStagedImageIds.push(mainImage.tempId);
      }
      stagedImages.forEach((img) => {
        if (!img.isMain) {
          orderedStagedImageIds.push(img.tempId);
        }
      });

      const dataToSend = { ...formData, stagedImageTempIds: orderedStagedImageIds };
      handleSubmit(e, dataToSend); // Pass the modified data to the hook
    } else {
      // For existing properties, just send the formData
      handleSubmit(e, formData);
    }
  };

  // --- Image Handling Logic is now in usePropertyImages hook ---
  const { stagedImages, uploadingFilesList, handleImageUpload, handleImageDelete, handleDeleteStagedImage, handleSetMainStagedImage, handleSetMainImage, handleImageRotate, clearStagedImages } = usePropertyImages(
    formData,
    setFormData,
    addNotification,
    user
  );

  const activatePromotion = async (orderId) => {
    if (!formData.propertyID) {
      addNotification('Cannot activate promotion: Property ID is missing.', 'error');
      return;
    }

    try {
      const url = `${BASE_URL}/api/promotions/property/${formData.propertyID}/activate-promotion`;
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId }),
      };

      const response = await apiFetch(url, options, user.token);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to activate promotion.');
      }

      addNotification(data.message || 'Promotion activated successfully!', 'success');
      setIsPromoteModalOpen(false);
      fetchProperty(); // Re-fetch property to show updated status
    } catch (error) {
      console.error('Error activating promotion:', error);
      addNotification(`Activation failed: ${error.message}`, 'error');
      setIsPromoteModalOpen(false);
    }
  };

  const handlePromotionPurchaseSuccess = (order) => {
    // This is called for credit-only purchases.
    // It will call the new backend endpoint to activate the promotion.
    addNotification('Promotion purchased with credits! Activating...', 'info');
    if (order && order._id) {
      activatePromotion(order._id);
    } else {
      addNotification('Activation failed: Order details are missing.', 'error');
      setIsPromoteModalOpen(false);
    }
  };

  const handlePromoteClick = () => {
    setIsPromoteModalOpen(true);
  };

  const formatRoiForDisplay = (roi) => {
    if (!roi || (roi.min === null && roi.max === null && roi.min === undefined && roi.max === undefined)) return null;
    const min = roi?.min !== null && roi?.min !== undefined ? `${roi.min.toFixed(1)}%` : null;
    const max = roi?.max !== null && roi?.max !== undefined ? `${roi.max.toFixed(1)}%` : null;
    if (min && max) {
      if (min === max) return min;
      return `${min} - ${max}`;
    }
    return min || max || null;
  };
  const displayableRoi = formData ? formatRoiForDisplay(formData.roi) : null;

  if (isLoading || isConfigLoading) {
    return <p>Loading data...</p>;
  }

  if (!formData) {
    return <p>Initializing...</p>;
  }

  const isCurrentlyFeatured = formData && formData.featured > 0 && formData.featuredUntil && new Date(formData.featuredUntil) > new Date();
  const isReadOnly = mode === 'view';
  const hasCache = formData.cachedReports && Object.keys(formData.cachedReports).length > 0;
  const mostRecentCacheTime = hasCache ? new Date(Math.max(...Object.values(formData.cachedReports).map((r) => new Date(r.time)))) : null;

  return (
    <div className="property-form-container">
      <div className="property-container">
        <div className="property-form-header">
          <div className="title-section">
            {user.isAdmin && (mode === 'view' || mode === 'edit') && formData && formData.sellerUsername && (
              <div className="seller-info-display">
                Seller: {formData.sellerFullName || 'N/A'} ({formData.sellerUsername})
              </div>
            )}
            <h2>
              {mode === 'add' ? (
                'Add New Property'
              ) : mode === 'edit' || mode === 'view' ? (
                <>
                  <span className="title-property-id">(ID: {formData.propertyID || 'N/A'})</span> {formData.address || 'Property Details'}
                  {displayableRoi && <span className="title-property-roi">(ROI: {displayableRoi})</span>}
                </>
              ) : (
                'Property'
              )}
            </h2>
            {/* Display Property Statistics if they exist (only sent for owner/admin) */}
            {formData.stats && <PropertyStatsDisplay stats={formData.stats} />}
          </div>
          <div className="admin-buttons">
            {mode === 'view' && user.isSeller && formData && (user.isAdmin || user.username === formData.sellerUsername) && (
              <button className="edit-btn" onClick={() => onEdit(formData)}>
                Edit
              </button>
            )}
            {mode === 'view' && user.isAdmin && (
              <button className="delete-btn" onClick={() => handleDelete(propertyID || formData.propertyID)}>
                Delete
              </button>
            )}
            {formData && (
              <button type="button" className="preview-btn" onClick={handlePreviewClick}>
                Preview
              </button>
            )}
            {mode !== 'add' && user && user.isLoggedIn && user.username === formData.sellerUsername && (
              <button type="button" className="promote-btn" onClick={handlePromoteClick}>
                {isCurrentlyFeatured ? 'Extend Promotion...' : 'Promote...'}
              </button>
            )}
            {mode !== 'add' && user.isAdmin && hasCache && (
              <button type="button" className="delete-btn" onClick={handleClearCache} title={`Most recent cached report from: ${mostRecentCacheTime ? format(mostRecentCacheTime, 'PPpp') : 'N/A'}`}>
                Clear Report Cache ({mostRecentCacheTime ? format(mostRecentCacheTime, 'P') : '...'})
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="form-error" title={error}>
            {error}
          </div>
        )}

        <Tabs tabsConfig={tabDefinitions} activeTab={activeTab} onTabClick={handleTabClick} tabErrors={isReadOnly ? {} : tabErrors} />

        <form onSubmit={handleFormSubmit}>
          <PropertyFields
            activeTab={activeTab}
            formData={formData}
            handleChange={handleChange}
            handleAddressSelect={handleAddressSelect}
            isReadOnly={isReadOnly}
            currentYear={currentYear}
            mode={mode}
            displayAddress={displayAddress}
            fieldErrors={isReadOnly ? {} : tabErrors[activeTab] || {}}
            onImageUpload={handleImageUpload}
            onImageDelete={handleImageDelete}
            onDeleteStagedImage={handleDeleteStagedImage} // For staged images
            uploadingFiles={uploadingFilesList}
            showAppNotification={addNotification}
            stagedImages={stagedImages} // Pass staged images down
            onImageRotate={handleImageRotate}
            onSetMainImage={handleSetMainImage}
            onSetMainStagedImage={handleSetMainStagedImage}
            user={user}
          />
          {!isReadOnly && (
            <div className="button-group">
              <button type="submit" className="subscribe-btn">
                {mode === 'add' ? 'Add Property' : 'Update Property'}
              </button>
              <button type="button" className="cancel-btn" onClick={handleCancelClick}>
                Cancel
              </button>
            </div>
          )}
        </form>
        {/* {!isReadOnly && formData.location?.coordinates?.length === 2 && (
          <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#555' }}>
            Coords: [{formData.location.coordinates[1].toFixed(6)}, {formData.location.coordinates[0].toFixed(6)}]
          </div>
        )} */}

        {/* Use the new PropertyPage for previews */}
        {isPreviewOpen && (
          <div className="property-preview-modal-container">
            {' '}
            {/* Wrapper for modal styling */}
            <PropertyPage isPreviewMode={true} propertyData={formData} onClose={() => setIsPreviewOpen(false)} />
          </div>
        )}

        <PurchaseModal
          isOpen={isPromoteModalOpen}
          onClose={() => setIsPromoteModalOpen(false)}
          purchaseType="promotion"
          propertyId={formData.propertyID}
          onPurchaseSuccess={handlePromotionPurchaseSuccess}
          featuredUntil={formData.featuredUntil} // Pass featuredUntil to the modal
        />
      </div>
    </div>
  );
};

export default PropertyForm;
